/**
 * Cours du membre (GET client/visits, GET client/visits/history) pour Mon compte v2 :
 * SPEC MC1 (cours à venir), MC6 (3 derniers cours), VC1 à VC3 (« Voir tous mes cours »).
 */
import type { ClientVisit } from 'types/client';
import { monthKey, toDate } from './format';

/** Nombre de prochains cours montrés sur Mon compte (= VisitsList.tsx de la v1 : 4). */
export const UPCOMING_ON_ACCOUNT = 4;

/**
 * « Mon historique » de Mon compte : les 3 derniers cours (SPEC MC6). Retour de Ségolène du 15/10 (RIISE et Dynamo) :
 * 10 lignes repoussaient le bloc Parrainage loin sous l'historique sur mobile ; « Voir tous mes cours » donne le reste.
 */
export const HISTORY_ON_ACCOUNT = 3;

/**
 * Séance importée de l'ancien système de réservation (Zingfit) : Mindbody la nomme « Converted Visit », sans le nom du
 * cours ; son lieu et son coach ne sont pas fiables (lib/stats.ts). Retour de Ségolène du 15/10 : « converted visit »
 * affiché dans l'historique.
 */
export function isConvertedVisit(visit: Pick<ClientVisit, 'Name'>): boolean {
  return (visit.Name ?? '').trim().toLowerCase() === 'converted visit';
}

/** Nom affiché d'un cours : « Cours » pour une séance importée de l'ancien système. */
export function visitName(visit: Pick<ClientVisit, 'Name'>): string {
  return isConvertedVisit(visit) ? 'Cours' : visit.Name;
}

/** À la place de « studio · coach » (non fiables) pour une séance importée de l'ancien système. */
export const CONVERTED_VISIT_NOTE = 'Ancien système de réservation';

/** Taille de page de « Voir tous mes cours » (SPEC VC2 : 20 ou 50). */
export const HISTORY_PER_PAGE = 20;

/** Début en ms ; NaN si la date est illisible (la visite est alors écartée de toutes les listes). */
const start = (visit: Pick<ClientVisit, 'StartDateTime'>) => toDate(visit.StartDateTime)?.getTime() ?? Number.NaN;

/**
 * = UserProfile.tsx Sessions() `futureVisits` : visites à venir, filtrées par site (« Filtrer par
 * marque »), sans les « Cross Region Visit » (VisitType -1), de la plus proche à la plus lointaine.
 */
export function upcomingVisits(
  visits: ClientVisit[] | undefined,
  siteId: number | null = null,
  now: Date = new Date(),
): ClientVisit[] {
  if (!visits) return [];
  return visits
    .filter((visit) => (siteId ? visit.SiteId === siteId : true))
    .filter((visit) => visit.VisitType !== -1)
    .filter((visit) => start(visit) > now.getTime())
    .sort((a, b) => start(a) - start(b));
}

/**
 * Sites (marques) des cours à venir : options du filtre « par marque » de « Mes cours à venir », site
 * visité d'abord, puis par Id. Le filtre ne s'affiche qu'à partir de deux marques.
 */
export function upcomingSiteIds(
  visits: ClientVisit[] | undefined,
  currentSiteId: string | number | undefined,
  now: Date = new Date(),
): number[] {
  const current = Number(currentSiteId);
  const ids = [...new Set(upcomingVisits(visits, null, now).map((visit) => Number(visit.SiteId)))];
  return ids.sort((a, b) => Number(b === current) - Number(a === current) || a - b);
}

/** Visites passées, sans les « Cross Region Visit », de la plus récente à la plus ancienne. */
export function pastVisits(visits: ClientVisit[] | undefined, now: Date = new Date()): ClientVisit[] {
  if (!visits) return [];
  return visits
    .filter((visit) => visit.VisitType !== -1)
    .filter((visit) => start(visit) <= now.getTime())
    .sort((a, b) => start(b) - start(a));
}

/** Les `n` derniers cours passés (SPEC MC6 : tranche côté client des visites déjà chargées). */
export function lastVisits(visits: ClientVisit[] | undefined, n = HISTORY_ON_ACCOUNT, now: Date = new Date()) {
  return pastVisits(visits, now).slice(0, n);
}

export type VisitOutcome = 'show' | 'noshow' | 'late' | 'cancelled' | 'none';

/**
 * Statut d'un cours passé. = VisitsHistoryItem.tsx (v1) : pas de statut pour une visite sans cours
 * (ClassId 0, import), « Annulée » si annulée, « Show » si pointée et pas absente, sinon « No Show ».
 * Ajout v2 : une annulation tardive (LateCancelled) est dite telle, plutôt que « No Show ».
 */
export function visitOutcome(visit: ClientVisit): VisitOutcome {
  if (visit.ClassId === 0) return 'none';
  if (visit.AppointmentStatus === 'Cancelled') return 'cancelled';
  if (visit.LateCancelled) return 'late';
  return !visit.Missed && visit.SignedIn ? 'show' : 'noshow';
}

export const OUTCOME_LABEL: Record<VisitOutcome, { label: string; tone: 'green' | 'red' | 'amber' | 'neutral' } | null> = {
  show: { label: 'Show', tone: 'green' },
  noshow: { label: 'No Show', tone: 'red' },
  late: { label: 'Annulation tardive', tone: 'amber' },
  cancelled: { label: 'Annulée', tone: 'neutral' },
  none: null,
};

/** Filtre de période de « Voir tous mes cours » : année seule (« 2026 ») ou mois (« 2026-03 »). */
export interface HistoryPeriod {
  year: string;
  month: string;
}

/** Paramètres `from` / `to` (AAAA-MM) du contrat GET client/visits/history (SPEC § 5.3). */
export function periodRange(period: HistoryPeriod): { from?: string; to?: string } {
  if (!period.year) return {};
  if (period.month) return { from: `${period.year}-${period.month}`, to: `${period.year}-${period.month}` };
  return { from: `${period.year}-01`, to: `${period.year}-12` };
}

/** Réponse de GET client/visits/history (SPEC § 5.3). */
export interface VisitsHistoryPage {
  items: ClientVisit[];
  total: number;
  page: number;
  perPage: number;
  /** Mois disponibles (AAAA-MM), du plus récent au plus ancien. */
  months: string[];
}

/**
 * Même découpage que la route GET client/visits/history, calculé dans le navigateur à partir de
 * GET client/visits : repli tant que la route n'existe pas sur le serveur du site (ou en erreur).
 * Passé seulement, sans VisitType -1, du plus récent au plus ancien.
 */
export function paginateHistory(
  visits: ClientVisit[] | undefined,
  { page, perPage, from, to }: { page: number; perPage: number; from?: string; to?: string },
  now: Date = new Date(),
): VisitsHistoryPage {
  const past = pastVisits(visits, now);
  const months = Array.from(new Set(past.map((v) => monthKey(toDate(v.StartDateTime) as Date))));
  const inRange = past.filter((v) => {
    const key = monthKey(toDate(v.StartDateTime) as Date);
    return (!from || key >= from) && (!to || key <= to);
  });
  const safePage = Math.max(1, page);
  return {
    items: inRange.slice((safePage - 1) * perPage, safePage * perPage),
    total: inRange.length,
    page: safePage,
    perPage,
    months,
  };
}

/** Années et mois proposés par le filtre, d'après la liste `months` (AAAA-MM). */
export function periodOptions(months: string[]): { years: string[]; monthsByYear: Record<string, string[]> } {
  const monthsByYear: Record<string, string[]> = {};
  months.forEach((key) => {
    const [year, month] = key.split('-');
    if (!year || !month) return;
    (monthsByYear[year] ??= []).push(month);
  });
  const years = Object.keys(monthsByYear).sort((a, b) => b.localeCompare(a));
  years.forEach((y) => monthsByYear[y].sort((a, b) => b.localeCompare(a)));
  return { years, monthsByYear };
}

/** Nombre de pages. */
export function pageCount(total: number, perPage: number): number {
  return Math.max(1, Math.ceil(total / Math.max(1, perPage)));
}
